import React, { useCallback } from 'react';
import { Checkbox } from 'antd';
import { DraggableProvided } from 'react-beautiful-dnd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import FormInput from 'components/Input/FormInput';
import Text from 'components/Text/Text';
import classNames from 'classnames';
import style from './DraggableModifierGroup.module.scss';

interface IDraggableModifierGroup {
	draggableInnerRef: ((element?: HTMLElement | null | undefined) => any) | undefined;
	changeDownLimit: (id: string, value: Nullable<number>) => void;
	changeUpLimit: (id: string, value: Nullable<number>) => void;
	toggleIsLimitsProtected: (id: string, value: boolean) => void;
	toggleIsAdditional: (id: string, value: boolean) => void;
	onDelete: (id: string) => void;
	provided: DraggableProvided;
	positionNumber: number;
	label: string;
	modifierGroupId: string;
	isLimitsProtected: boolean;
	isAdditional: boolean;
	hasDragAccess: boolean;
}

export const DraggableModifierGroup: React.FC<IDraggableModifierGroup> = React.memo(
	({
		draggableInnerRef,
		changeDownLimit,
		changeUpLimit,
		toggleIsLimitsProtected,
		toggleIsAdditional,
		onDelete,
		provided,
		positionNumber,
		label,
		modifierGroupId,
		isLimitsProtected,
		isAdditional,
		hasDragAccess,
	}) => {
		const dragMarkClasses = classNames(style.dragMark, { [style.hidden]: !hasDragAccess });

		const handleRemove = () => {
			onDelete(modifierGroupId);
		};

		const handleChangeDownLimit = (value: Nullable<number>) => {
			changeDownLimit(modifierGroupId, value);
		};

		const handleChangeUpLimit = (value: Nullable<number>) => {
			changeUpLimit(modifierGroupId, value);
		};

		const handleToggleIsLimitsProtected = (e: CheckboxChangeEvent) => {
			toggleIsLimitsProtected(modifierGroupId, e.target.checked);
		};

		const handleToggleIsAdditional = (e: CheckboxChangeEvent) => {
			toggleIsAdditional(modifierGroupId, e.target.checked);
		};

		return (
			<div
				className={style.container}
				ref={hasDragAccess ? draggableInnerRef : undefined}
				{...provided.draggableProps}>
				<div className={dragMarkClasses} {...provided.dragHandleProps}>
					<svg>
						<use xlinkHref={`${breadcrumbs}#dragMark`} />
					</svg>
				</div>
				<div className={style.content}>
					<div className={style.labelRow}>
						<Text
							className={style.positionNumber}
							translationNameSpace="form"
							translationKey="positionNumberLabel"
							translationOptions={{ positionNumber }}
							theme="secondary"
							size="s"
							weight="bold"
						/>
						<Text
							className={style.labelText}
							translationKey={label}
							theme="secondary"
							size="xs"
							withTooltip={true}
						/>
						<div className={style.deleteBtn} onClick={handleRemove}>
							<svg className={style.deleteIcon}>
								<use xlinkHref={`${breadcrumbs}#closeSelectItem`} />
							</svg>
						</div>
					</div>
					<div className={style.limits}>
						<FormInput
							testId="LowerLimitField"
							name={`downLimit-${modifierGroupId}`}
							label="lowerLimitLabel"
							min={0}
							precision={0}
							onChangeNumber={handleChangeDownLimit}
							type="number"
						/>
						<FormInput
							testId="UpperLimitField"
							name={`upLimit-${modifierGroupId}`}
							label="upperLimitLabel"
							min={0}
							precision={0}
							onChangeNumber={handleChangeUpLimit}
							type="number"
						/>
					</div>
					<div>
						<Checkbox
							data-test="IsLimitsProtectedFromSyncCheckbox"
							name={`isLimitsProtected-${modifierGroupId}`}
							className={style.checkbox}
							onChange={handleToggleIsLimitsProtected}
							checked={isLimitsProtected}>
							<Text translationNameSpace="form" translationKey="protectFromSyncLabel" asFragment={true} />
						</Checkbox>
						<Checkbox
							data-test="isAdditional"
							name={`isAdditional-${modifierGroupId}`}
							className={style.checkbox}
							onChange={handleToggleIsAdditional}
							checked={isAdditional}>
							<Text translationNameSpace="form" translationKey="useAsAdditionalLabel" asFragment={true} />
						</Checkbox>
					</div>
				</div>
			</div>
		);
	},
);
